/**
 * Asset alert actions (M6 plan P35): binds P22's `bindFleetAlertHooks` and keeps the flight HUD's action strip.
 *
 * - **Hooks:** *jump to* opens the galaxy map focused on the alert's system (`openMap`); *send wing* orders the wing
 *   to break and engage (`commandWing('breakEngage')`). `dispatchAlert` applies this feature's rule
 *   (`offersAction`) before P22's `runAlertAction`, so a hidden action never runs.
 * - **Strip:** the newest alert that offers an action, held `alertActionS` after it arrived, read from the fleet alert
 *   inbox with its own cursor (independent of the toast queue). The DOM is written only when the shown alert or its
 *   offered actions change; the step allocates nothing.
 * - **History:** the last `alertKeep` alerts of every kind, for the Fleet feed panel's Alerts list and its buttons
 *   (message rate). Before D-459 only actionable alerts were kept, so a captain's death never reached the panel.
 */
import { alertAt, alertOffers, bindFleetAlertHooks, firstUnread, runAlertAction, type FleetAlertAction, type FleetAlertInbox } from '../../fleet-link/presence'
import type { FleetAlert } from '../../strategic/types'
import type { WingCommandName } from '../../wing/wing'
import { actionText, offersAction, type AlertContext, type AlertKeys, type FleetFeedConfig } from './model'

export interface AlertActionDeps {
  /** Opens the galaxy map focused on a system. */
  openMap(systemId: string): void
  /** Commands the live wing; false when there is none. */
  commandWing(cmd: WingCommandName): boolean
}

/** Binds the P22 hooks; returns the unbind. */
export function bindAlertActions(deps: AlertActionDeps): () => void {
  bindFleetAlertHooks({
    jumpTo: (a) => deps.openMap(a.systemId),
    sendWing: () => {
      deps.commandWing('breakEngage')
    },
  })
  return () => bindFleetAlertHooks({ jumpTo: null, sendWing: null })
}

/** Runs `action` for `alert` when this feature's rule offers it now; true when its hook ran. */
export function dispatchAlert(alert: Readonly<FleetAlert>, action: FleetAlertAction, ctx: Readonly<AlertContext>): boolean {
  if (!offersAction(alert, action, ctx)) return false
  return runAlertAction(alert, action)
}

export interface AlertStrip {
  cursor: number
  /** The alert the strip holds, null when none. */
  alert: FleetAlert | null
  age: number
  /** Recent alerts with an action (newest first), for the panel. */
  history: FleetAlert[]
  /** What the DOM shows (diffed). */
  shown: FleetAlert | null
  shownJump: boolean
  shownWing: boolean
}

export function createAlertStrip(inbox: FleetAlertInbox): AlertStrip {
  return { cursor: inbox.head, alert: null, age: 0, history: [], shown: null, shownJump: false, shownWing: false }
}

/**
 * Reads new alerts, ages and expires the strip's. Every alert with text goes into the panel's history (a captain's
 * death, a strike or late wages included: D-459 bug 2); only the newest with *jump to* or *send wing* by P22's rule
 * takes the strip.
 */
export function stepAlertStrip(s: AlertStrip, inbox: FleetAlertInbox, dt: number, cfg: FleetFeedConfig): void {
  for (let i = firstUnread(inbox, s.cursor); i < inbox.head; i++) {
    const a = alertAt(inbox, i)
    if (a === null) continue
    if (a.text !== '') {
      s.history.unshift(a)
      if (s.history.length > cfg.alertKeep) s.history.length = cfg.alertKeep
    }
    // Send-wing kinds are a subset of the jump-to kinds (P22), so P22's jump rule picks every actionable alert.
    if (!alertOffers(a, 'jumpTo')) continue
    s.alert = a
    s.age = 0
  }
  s.cursor = inbox.head
  if (s.alert === null) return
  s.age += dt
  if (s.age >= cfg.alertActionS) s.alert = null
}

/** The strip's DOM (a line under the Reach HUD toasts). */
export interface AlertStripView {
  readonly el: HTMLElement
  /** DOM writes so far (tests). */
  writes: number
}

export function createAlertStripView(parent: HTMLElement): AlertStripView {
  const el = parent.ownerDocument.createElement('div')
  el.className = 'ff-alert'
  el.dataset.on = '0'
  el.setAttribute('data-testid', 'fleet-alert-actions')
  parent.appendChild(el)
  return { el, writes: 0 }
}

/** Writes the strip only when the alert or its offered actions changed. */
export function writeAlertStrip(v: AlertStripView, s: AlertStrip, ctx: Readonly<AlertContext>, cfg: FleetFeedConfig, keys: Readonly<AlertKeys>): void {
  const a = s.alert
  const jump = a !== null && offersAction(a, 'jumpTo', ctx)
  const wing = a !== null && offersAction(a, 'sendWing', ctx)
  if (a === s.shown && jump === s.shownJump && wing === s.shownWing) return
  s.shown = a
  s.shownJump = jump
  s.shownWing = wing
  v.writes++
  if (a === null || !(jump || wing)) {
    v.el.dataset.on = '0'
    v.el.textContent = ''
    return
  }
  v.el.dataset.on = '1'
  v.el.dataset.kind = a.kind
  v.el.dataset.wing = wing ? '1' : '0'
  v.el.textContent = actionText(a, ctx, cfg, keys)
}
